import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { DEFAULT_PLANS, DEFAULT_SETTINGS } from '../config/defaultPlans';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import './PlansView.css';

const EMPTY = { planId: '', label: '', maxChannels: 1, maxAgents: 1, maxMessagesPerMonth: 1000, audio: true, order: 0, archived: false };
const fmtLimit = (n) => (Number(n) === -1 ? '∞' : n);

export default function PlansView({ onBack, onLogout }) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null); // null = creating
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formError, setFormError] = useState('');
  const [settingsError, setSettingsError] = useState('');

  const fetchData = async () => {
    const [plansSnap, setSnap] = await Promise.all([
      getDocs(collection(db, 'plans')),
      getDoc(doc(db, 'settings', 'billing')),
    ]);
    setPlans(plansSnap.docs.map(d => ({ planId: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
    if (setSnap.exists()) setSettings({ graceDays: setSnap.data().graceDays, trialDays: setSnap.data().trialDays });
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const savePlanDoc = async (payload) => {
    await httpsCallable(functions, 'savePlan')({
      planId: payload.planId,
      label: payload.label,
      maxChannels: Number(payload.maxChannels),
      maxAgents: Number(payload.maxAgents),
      maxMessagesPerMonth: Number(payload.maxMessagesPerMonth),
      features: { audio: !!payload.audio },
      archived: !!payload.archived,
      order: Number(payload.order) || 0,
    });
  };

  const startNew = () => { setEditingId(null); setForm(EMPTY); };
  const startEdit = (p) => {
    setEditingId(p.planId);
    setForm({ planId: p.planId, label: p.label || '', maxChannels: p.maxChannels, maxAgents: p.maxAgents, maxMessagesPerMonth: p.maxMessagesPerMonth, audio: !!p.features?.audio, order: p.order || 0, archived: !!p.archived });
  };

  const handleSave = async () => {
    if (!editingId && !form.planId.trim() && !form.label.trim()) {
      setFormError(t('plans.needIdOrName'));
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      await savePlanDoc(form);
      startNew();
      await fetchData();
    } catch (err) {
      console.error(err);
      setFormError(t('plans.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async (p) => {
    try {
      await savePlanDoc({ ...p, audio: p.features?.audio, archived: !p.archived });
      await fetchData();
    } catch (err) {
      console.error(err);
      setFormError(t('plans.errors.archive'));
    }
  };

  const importDefaults = async () => {
    setFormError('');
    setSaving(true);
    try {
      for (const p of DEFAULT_PLANS) await savePlanDoc({ ...p, audio: p.features.audio });
      await httpsCallable(functions, 'saveBillingSettings')(DEFAULT_SETTINGS);
      await fetchData();
    } catch (err) {
      console.error(err);
      setFormError(t('plans.errors.import'));
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSettingsError('');
    setSavingSettings(true);
    try {
      await httpsCallable(functions, 'saveBillingSettings')({
        graceDays: Number(settings.graceDays),
        trialDays: Number(settings.trialDays),
      });
      await fetchData();
    } catch (err) {
      console.error(err);
      setSettingsError(t('plans.errors.saveSettings'));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={[t('plans.crumb')]} />
      <main className="page-main">
        <div className="pv-head">
          <h1>{t('plans.title')}</h1>
          <p className="pv-sub">{t('plans.subtitle')}</p>
        </div>

        {loading ? (
          <div className="card"><Loading label={t('plans.loading')} /></div>
        ) : (
          <>
            {plans.length === 0 && (
              <div className="card pv-empty">
                {t('plans.empty')}
                <button className="btn btn-primary btn-sm" onClick={importDefaults} disabled={saving}>
                  <Icon name="package" size={16} />{saving ? t('plans.importing') : t('plans.importDefaults')}
                </button>
              </div>
            )}

            <div className="card pv-form">
              <h3>{editingId ? t('plans.editPlan', { id: editingId }) : t('plans.newPlan')}</h3>
              <div className="pv-grid">
                {!editingId && (
                  <label>{t('plans.slugLabel')}
                    <input className="field" placeholder={t('plans.slugPlaceholder')} value={form.planId} onChange={e => upd('planId', e.target.value)} />
                  </label>
                )}
                <label>{t('plans.nameLabel')}
                  <input className="field" placeholder={t('plans.namePlaceholder')} value={form.label} onChange={e => upd('label', e.target.value)} />
                </label>
                <label>{t('plans.maxChannels')}
                  <input className="field" type="number" value={form.maxChannels} onChange={e => upd('maxChannels', e.target.value)} />
                </label>
                <label>{t('plans.maxAgents')}
                  <input className="field" type="number" value={form.maxAgents} onChange={e => upd('maxAgents', e.target.value)} />
                </label>
                <label>{t('plans.maxMessages')}
                  <input className="field" type="number" value={form.maxMessagesPerMonth} onChange={e => upd('maxMessagesPerMonth', e.target.value)} />
                </label>
                <label>{t('plans.order')}
                  <input className="field" type="number" value={form.order} onChange={e => upd('order', e.target.value)} />
                </label>
                <label className="pv-check">
                  <input type="checkbox" checked={form.audio} onChange={e => upd('audio', e.target.checked)} />
                  {t('plans.audioTranscription')}
                </label>
              </div>
              <div className="pv-form-actions">
                {editingId && <button className="btn btn-ghost" onClick={startNew}>{t('common.cancel')}</button>}
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  <Icon name="check" size={16} />{saving ? t('plans.saving') : (editingId ? t('plans.saveChanges') : t('plans.createPlan'))}
                </button>
              </div>
              {formError && <p className="form-error">{formError}</p>}
            </div>

            <div className="pv-list">
              {plans.map(p => (
                <div className={`card pv-item ${p.archived ? 'archived' : ''}`} key={p.planId}>
                  <div className="pv-info">
                    <div className="pv-name">
                      {p.label || p.planId}
                      <span className="pv-id">{p.planId}</span>
                      {p.archived && <span className="pv-badge">{t('plans.archivedBadge')}</span>}
                    </div>
                    <div className="pv-meta">
                      {t('plans.metaLine', { channels: fmtLimit(p.maxChannels), agents: fmtLimit(p.maxAgents), messages: fmtLimit(p.maxMessagesPerMonth), audio: p.features?.audio ? 'on' : 'off' })}
                    </div>
                  </div>
                  <div className="pv-row-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => startEdit(p)}>
                      <Icon name="edit" size={15} />{t('common.edit')}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => toggleArchive(p)}>
                      <Icon name={p.archived ? 'rotate' : 'trash'} size={15} />{p.archived ? t('plans.reactivate') : t('plans.archive')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="card pv-settings">
              <h3>{t('plans.globalParams')}</h3>
              <div className="pv-grid">
                <label>{t('plans.graceDays')}
                  <input className="field" type="number" min="0" value={settings.graceDays} onChange={e => setSettings(s => ({ ...s, graceDays: e.target.value }))} />
                </label>
                <label>{t('plans.trialDays')}
                  <input className="field" type="number" min="0" value={settings.trialDays} onChange={e => setSettings(s => ({ ...s, trialDays: e.target.value }))} />
                </label>
              </div>
              <div className="pv-form-actions">
                <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                  <Icon name="check" size={16} />{savingSettings ? t('plans.saving') : t('plans.saveParams')}
                </button>
              </div>
              {settingsError && <p className="form-error">{settingsError}</p>}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
